import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { CATEGORIES } from "@/constants";
import { ArrowUpRight, MessageCircle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import OrderDialog from "@/components/OrderDialog";
import { categoryWhatsappLink } from "@/lib/whatsapp";

export default function CategoriesGrid() {
  const { t } = useI18n();
  const [activeCategory, setActiveCategory] = useState(null);

  return (
    <section id="catalog" data-testid="categories-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl">
          <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light">
            {t.catalog.eyebrow}
          </div>
          <h2 className="font-heading mt-3 text-4xl sm:text-5xl font-black tracking-tighter">
            {t.catalog.title}
          </h2>
          <p className="mt-4 text-zinc-400 text-base leading-relaxed max-w-2xl">
            {t.catalog.subtitle}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((cat, i) => (
            <CategoryCard
              key={cat.key}
              cat={cat}
              index={i}
              onRequest={() => setActiveCategory(cat.key)}
              t={t}
            />
          ))}
        </div>
      </div>

      <OrderDialog
        open={!!activeCategory}
        category={activeCategory}
        onClose={() => setActiveCategory(null)}
      />
    </section>
  );
}

function CategoryCard({ cat, index, onRequest, t }) {
  const meta = t.categories[cat.key];
  const isInStock = cat.key === "in_stock";
  const handleOrder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRequest();
  };
  const handleWhats = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(categoryWhatsappLink(meta.name), "_blank", "noopener,noreferrer");
  };
  return (
    <Link
      to={`/category/${cat.key}`}
      data-testid={`category-card-${cat.key}`}
      className="group relative bg-zinc-900/50 border border-white/5 hover:border-brand-blue/50 hover:shadow-[0_0_32px_rgba(0,85,255,0.18)] transition-all duration-300 flex flex-col cursor-pointer"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative h-60 overflow-hidden">
        <img
          src={cat.image}
          alt={meta.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute top-3 left-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-brand-blue-light bg-black/60 border border-brand-blue/30 px-2 py-1">
          #{cat.code}
        </div>
        {isInStock && (
          <div
            data-testid={`in-stock-badge-${cat.key}`}
            className="absolute top-3 right-3 inline-flex items-center gap-1.5 font-mono-accent text-[10px] font-bold uppercase tracking-[0.18em] text-black bg-amber-300 border border-amber-200 px-2.5 py-1 shadow-[0_0_18px_rgba(251,191,36,0.55)] animate-pulse"
          >
            <Sparkles size={11} />
            {t.catalog.in_stock_badge}
          </div>
        )}
        {!isInStock && (
          <div className="absolute top-3 right-3 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/80 bg-black/60 border border-white/15 px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            View Gallery <ArrowUpRight size={11} />
          </div>
        )}
        <div className="absolute bottom-3 left-4 right-4">
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            {meta.tag}
          </div>
          <div className="font-heading text-2xl font-extrabold mt-1">{meta.name}</div>
        </div>
      </div>

      <div className="p-6 flex flex-col gap-4 flex-1">
        <p className="text-sm text-zinc-400 leading-relaxed flex-1">{meta.desc}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid={`request-order-btn-${cat.key}`}
            onClick={handleOrder}
            className="inline-flex items-center justify-between gap-2 bg-brand-blue hover:bg-brand-blue-light text-white px-4 py-3 uppercase tracking-widest text-[11px] font-bold border border-brand-blue-light/40 transition-all font-mono-accent group/btn"
          >
            {t.catalog.cta}
            <ArrowUpRight size={14} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
          <button
            data-testid={`whatsapp-btn-${cat.key}`}
            onClick={handleWhats}
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-3 uppercase tracking-widest text-[11px] font-bold border border-emerald-300/60 transition-all font-mono-accent"
          >
            <MessageCircle size={14} />
            {t.catalog.whatsapp}
          </button>
        </div>
      </div>
    </Link>
  );
}
